<script lang="ts" setup>
import CreateCheckCallForm from "@/views/trips/components/forms/CreateCheckCallForm.vue";
import DispatchConfirmationForm from "@/views/trips/components/forms/DispatchConfirmationForm.vue";
import type { TripMonitorItem, TripMonitorRoute } from "@/api/trips/dto/trip-monitor";
import TripIntervalDialog from "@/views/trips/components/TripIntervalDialog.vue";
import { API } from "@/api/api";
import { useSnackbarStore } from "@/store/snackbar.store";
import { usePipStore } from "@/store/pip.store";
import { computed, reactive, ref, watch } from "vue";
import type { CheckCallDTO, CreateCheckCallRequest } from "@/api/trips/dto/check-call";
import { useError } from "@/composables/useError";
import type { CheckCallFormProps } from "@/types/props";

const api = new API();

const { showSnackbarSuccess, showSnackbarError } = useSnackbarStore();
const { showAPIError } = useError();
const pipStore = usePipStore();

const showAddCheckCallDialog = defineModel<boolean>("showAddCheckCallDialog", { default: false });
const showConfirmDispatchDialog = defineModel<boolean>("showConfirmDispatchDialog", { default: false });
const showIntervalDialog = defineModel<boolean>("showIntervalDialog", { default: false });
const props = defineProps<{
  tripMonitorRoute: TripMonitorRoute;
  trip: TripMonitorItem;
}>();

const checkCallPreview = ref<CheckCallDTO | null>(null);
const creationLoading = ref<boolean>(false);

const routeStops = computed(() => (props.tripMonitorRoute.nextRouteStop ? [props.tripMonitorRoute.nextRouteStop] : []));

const createCheckCallTitle = computed(() => {
  return "New check call for truck #" + props.tripMonitorRoute.truck.number;
});

const confirmDispatchTitle = computed(() => {
  return Boolean(checkCallPreview.value) ? "Dispatch preview" : "Dispatch confirmation";
});

const addCheckCallProps = reactive<CheckCallFormProps>({
  get orders() {
    return props.trip.orders;
  },
  get routeId() {
    return props.tripMonitorRoute.id;
  },
  get routeStops() {
    return routeStops.value;
  },
  get creationLoading() {
    return creationLoading.value;
  },
  get checkCallPreview() {
    return checkCallPreview.value;
  },
  get title() {
    return createCheckCallTitle.value;
  },
});

const confirmDispatchProps = reactive<CheckCallFormProps>({
  get orders() {
    return props.trip.orders;
  },
  get routeId() {
    return props.tripMonitorRoute.id;
  },
  get routeStops() {
    return routeStops.value;
  },
  get creationLoading() {
    return creationLoading.value;
  },
  get checkCallPreview() {
    return checkCallPreview.value;
  },
  get tripId() {
    return props.trip.id;
  },
  get truckId() {
    return props.tripMonitorRoute.truck.id;
  },
  get truckNumber() {
    return props.tripMonitorRoute.truck.number;
  },
  get title() {
    return confirmDispatchTitle.value;
  },
});

function resetCheckCallPreview(): void {
  checkCallPreview.value = null;
}

async function openAddCheckCallPip(): Promise<void> {
  await pipStore.open({
    component: CreateCheckCallForm,
    props: addCheckCallProps,
    on: {
      create: createCheckCall,
      edit: resetCheckCallPreview,
      close: () => pipStore.close(),
    },
  });

  showAddCheckCallDialog.value = false;
}

async function openConfirmDispatchPip(): Promise<void> {
  await pipStore.open({
    component: DispatchConfirmationForm,
    props: confirmDispatchProps,
    on: {
      create: confirmDispatch,
      edit: resetCheckCallPreview,
      close: () => pipStore.close(),
    },
  });

  showConfirmDispatchDialog.value = false;
}

async function updateInterval(interval: number | null): Promise<void> {
  try {
    const response = await api.trips.updateRouteInterval(props.trip.id, props.tripMonitorRoute.id, interval);

    if (response.success) {
      showSnackbarSuccess("Interval saved successfully");
    }
  } catch (error) {
    console.log(error);
    showSnackbarError("Could not save the trip interval");
  }
}

async function createCheckCall(data: CreateCheckCallRequest) {
  creationLoading.value = true;

  try {
    const response = await api.events.createCheckCall(props.tripMonitorRoute.id, data);

    if (!response.success || !response.data) {
      showAPIError(response, "Error creating check call");

      return;
    }

    if (!data.commit) {
      checkCallPreview.value = response.data;
    }
  } catch (error) {
    console.error(error);
  } finally {
    creationLoading.value = false;
  }
}

async function confirmDispatch(data: CreateCheckCallRequest) {
  creationLoading.value = true;

  try {
    const response = await api.trips.confirmRouteDispatch(props.trip.id, props.tripMonitorRoute.id, data);

    if (!response.success || !response.data) {
      showAPIError(response, "Error dispatching route");

      return;
    }

    if (!data.commit) {
      checkCallPreview.value = response.data;
    }
  } catch (error) {
    console.error(error);
  } finally {
    creationLoading.value = false;
  }
}

watch(
  showAddCheckCallDialog,
  (show) => {
    if (!show) {
      return;
    }

    checkCallPreview.value = null;
    openAddCheckCallPip();
  },
  {
    immediate: true,
  },
);

watch(
  showConfirmDispatchDialog,
  (show) => {
    if (!show) {
      return;
    }

    checkCallPreview.value = null;
    openConfirmDispatchPip();
  },
  {
    immediate: true,
  },
);
</script>

<template>
  <trip-interval-dialog
    v-if="showIntervalDialog"
    v-model="showIntervalDialog"
    :interval="props.tripMonitorRoute.checkCallInterval"
    :truck-id="props.tripMonitorRoute.truck.id"
    :truck-number="props.tripMonitorRoute.truck.number"
    title="Route timer settings"
    @update-interval="updateInterval"
  />
</template>
